import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme,
  Alert,
  Tooltip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StoreIcon from '@mui/icons-material/Store';
import PersonIcon from '@mui/icons-material/Person';
import PhotoIcon from '@mui/icons-material/Photo';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import AssignmentIcon from '@mui/icons-material/Assignment';
import InfoIcon from '@mui/icons-material/Info';
import ClinicManager from './ClinicManager';
import PractitionerPhotoManager from './PractitionerPhotoManager';
import PractitionerAppointmentTypeManager from './PractitionerAppointmentTypeManager';
import BookingLogs from './BookingLogs';
import { logout } from '../services/adminService';

const Dashboard = ({ user, onLogout }) => {
  const [activeView, setActiveView] = useState('clinics');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const handleLogout = () => {
    logout();
    onLogout();
  };
  
  const handleMenuClick = (view) => {
    setActiveView(view);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };
  
  const [selectedClinicId, setSelectedClinicId] = useState(null);

  // Handle clinic selection
  const handleClinicSelect = (clinicId) => {
    setSelectedClinicId(clinicId);
    
    // Optionally redirect to the practitioner services page after selecting a clinic
    if (clinicId) {
      // Can uncomment this to auto-navigate to practitioner services
      // handleMenuClick('practitioner-services');
    }
  };
  
  const renderContent = () => {
    switch (activeView) {
      case 'clinics':
        return <ClinicManager onClinicSelect={handleClinicSelect} />;
      case 'practitioner-photos':
        return <PractitionerPhotoManager clinicId={selectedClinicId} />;
      case 'practitioner-services':
        return <PractitionerAppointmentTypeManager clinic={selectedClinicId} />;
      case 'booking-logs':
        return <BookingLogs />;
      case 'profile':
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>User Profile</Typography>
            <Typography><strong>Username:</strong> {user?.username}</Typography>
            <Typography><strong>Email:</strong> {user?.email}</Typography>
            <Typography><strong>Role:</strong> {user?.is_admin ? 'Administrator' : 'User'}</Typography>
          </Box>
        );
      default:
        return (
          <Box sx={{ p: 3 }}>
            <Typography>Welcome to the Drip Hydration Admin Dashboard</Typography>
          </Box>
        );
    }
  };
  
  const drawerContent = (
    <>
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          DH Bookings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Administration
        </Typography>
      </Box>
      <Divider />
      <List>
        <ListItem 
          button 
          selected={activeView === 'dashboard'} 
          onClick={() => handleMenuClick('dashboard')}
        >
          <ListItemIcon>
            <DashboardIcon />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItem>
        <ListItem 
          button 
          selected={activeView === 'clinics'} 
          onClick={() => handleMenuClick('clinics')}
        >
          <ListItemIcon>
            <StoreIcon />
          </ListItemIcon>
          <ListItemText primary="Clinics" />
        </ListItem>
        <Tooltip title={!selectedClinicId ? "Please select a clinic first" : ""} placement="right">
          <div style={{ width: '100%' }}>  {/* Wrapper div needed for disabled tooltips */}
            <ListItem 
              button 
              selected={activeView === 'practitioner-photos'} 
              onClick={() => handleMenuClick('practitioner-photos')}
              disabled={!selectedClinicId}
            >
              <ListItemIcon>
                <PhotoIcon />
              </ListItemIcon>
              <ListItemText primary="Practitioner Photos" />
            </ListItem>
          </div>
        </Tooltip>
        <Tooltip title={!selectedClinicId ? "Please select a clinic first" : ""} placement="right">
          <div style={{ width: '100%' }}>  {/* Wrapper div needed for disabled tooltips */}
            <ListItem 
              button 
              selected={activeView === 'practitioner-services'} 
              onClick={() => handleMenuClick('practitioner-services')}
              disabled={!selectedClinicId}
            >
              <ListItemIcon>
                <MedicalServicesIcon />
              </ListItemIcon>
              <ListItemText primary="Practitioner Services" />
            </ListItem>
          </div>
        </Tooltip>
        <ListItem 
          button 
          selected={activeView === 'booking-logs'} 
          onClick={() => handleMenuClick('booking-logs')}
        >
          <ListItemIcon>
            <AssignmentIcon />
          </ListItemIcon>
          <ListItemText primary="Booking Logs" />
        </ListItem>
      </List>
      <Divider />
      <List>
        <ListItem 
          button 
          selected={activeView === 'profile'} 
          onClick={() => handleMenuClick('profile')}
        >
          <ListItemIcon>
            <PersonIcon />
          </ListItemIcon>
          <ListItemText primary="Profile" />
        </ListItem>
        <ListItem button onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </>
  );
  
  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setDrawerOpen(!drawerOpen)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Drip Hydration Admin
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {user?.username}
          </Typography>
          <Button color="inherit" onClick={handleLogout} startIcon={<LogoutIcon />}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      
      {/* Sidebar */}
      {isMobile ? (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sx={{
            width: 240,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 240,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: 240,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 240,
              boxSizing: 'border-box',
            },
          }}
        >
          <Toolbar /> {/* Spacer for AppBar */}
          {drawerContent}
        </Drawer>
      )}
      
      {/* Main content */}
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar /> {/* Spacer for AppBar */}
        <Container maxWidth="lg">
          {!selectedClinicId && activeView !== 'clinics' && (
            <Box sx={{ mb: 3 }}>
              <Alert 
                severity="info" 
                icon={<InfoIcon />}
                action={
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={() => handleMenuClick('clinics')}
                  >
                    Select Clinic
                  </Button>
                }
              >
                Please select a clinic to access additional management options.
              </Alert>
            </Box>
          )}
          {renderContent()}
        </Container>
      </Box>
    </Box>
  );
};

export default Dashboard;